import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, BookOpen, Zap, ShieldCheck, HelpCircle, Monitor, Smartphone, Tv, Settings, Layout, Search } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia IPTV para Quem Nunca Utilizou", href: "/blog/guia-iptv-nunca-utilizou" }
  ];

  const faq = [
    {
      question: "O IPTV estraga a televisão?",
      answer: "Absolutamente não. O IPTV é apenas um aplicativo que roda no sistema da TV, assim como o YouTube ou a Netflix. Não há risco físico para o aparelho."
    },
    {
      question: "Preciso pagar mensalidade?",
      answer: "Sim, os serviços de alta performance como a P2 PRO funcionam no modelo de assinatura mensal, trimestral ou anual, garantindo a manutenção dos servidores e atualização de conteúdos."
    },
    {
      question: "Funciona se a internet cair?",
      answer: "Não. Como o próprio nome diz (Internet Protocol Television), a conexão com a web é indispensável para o funcionamento."
    }
  ];

  const benefits = [
    {
      title: "Aprendizado Acelerado",
      description: "Conteúdo focado em quem não tem nenhuma base técnica sobre o assunto.",
      icon: <BookOpen className="w-6 h-6" />
    },
    {
      title: "Simplicidade Extrema",
      description: "Linguagem clara e exemplos do dia a dia para facilitar o entendimento.",
      icon: <HelpCircle className="w-6 h-6" />
    },
    {
      title: "Resultado Garantido",
      description: "Siga o passo a passo e tenha sua TV funcionando hoje mesmo.",
      icon: <Check className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Entenda o Conceito",
      description: "Veja como a internet substitui o cabo da antena."
    },
    {
      title: "Identifique sua TV",
      description: "Saiba se sua televisão já está pronta para o IPTV."
    },
    {
      title: "Baixe o Aplicativo",
      description: "Encontre o player na loja oficial do seu aparelho."
    },
    {
      title: "Aperte o Play",
      description: "Insira os dados e comece a navegar pelos canais."
    }
  ];

  const diferenciais = [
    {
      title: "Linguagem Amigável",
      description: "Explicamos tudo como se estivéssemos conversando na sua sala.",
      icon: <Info className="w-6 h-6" />
    },
    {
      title: "Foco no Resultado",
      description: "O importante é você assistir o que gosta, sem complicação.",
      icon: <Zap className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. O Que Exatamente é IPTV? (Sem Termos Técnicos)",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Se você <strong>nunca utilizou IPTV</strong>, pense nele como uma evolução do rádio. Antigamente, você precisava de uma antena enorme no telhado para ouvir música. Hoje, você ouve tudo pelo Spotify ou YouTube usando apenas a internet. O IPTV faz exatamente a mesma coisa com a televisão. Ele pega o sinal dos canais, transforma em dados e envia para a sua TV pelo seu Wi-Fi.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Na <strong>P2 PRO</strong>, nós somos como a central que organiza todos esses dados. Nós pegamos milhares de canais, filmes e séries e organizamos tudo em um 'aplicativo' para que você possa escolher o que assistir com um simples clique no controle remoto. É simples, é moderno e, o melhor de tudo, é muito mais barato que as TVs por assinatura tradicionais.
          </p>
        </div>
      )
    },
    {
      title: "2. Do Que Você Precisa Para Começar Agora?",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Check className="w-6 h-6 text-electric" /> O Básico</h4>
              <p className="text-sm text-muted-foreground">Você só precisa de duas coisas: Uma conexão de internet estável e um aparelho para assistir (TV, Celular ou Tablet).</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Search className="w-6 h-6 text-amber-500" /> A Escolha</h4>
              <p className="text-sm text-muted-foreground">Escolher um bom fornecedor é vital. Na P2 PRO, oferecemos teste grátis justamente para quem nunca usou poder experimentar sem medo.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo Para a Sua Primeira Vez",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Não tenha medo dos botões. Siga este roteiro simplificado:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Conecte sua TV à Internet</h4>
              <p className="text-muted-foreground">Verifique se o YouTube está funcionando na sua TV. Se estiver, o IPTV também funcionará.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Chame Nosso Suporte</h4>
              <p className="text-muted-foreground">Não tente configurar sozinho se tiver dúvidas. Nossa equipe enviará um vídeo curto ensinando exatamente onde clicar no modelo da sua TV.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas Para Não Ter Dor de Cabeça",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para quem está começando hoje, estas três dicas valem ouro:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Prefira o Wi-Fi de 5GHz</h5>
                <p className="text-sm text-muted-foreground">Se sua TV estiver longe do roteador, o sinal de 5GHz (geralmente aparece com o nome da sua rede + 5G) é muito mais rápido e estável para vídeos.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Não compre aparelhos caros agora</h5>
                <p className="text-sm text-muted-foreground">Muitos iniciantes compram TV Boxes caríssimas sem necessidade. Teste primeiro na sua Smart TV. Na maioria das vezes, ela já é suficiente.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">O teste é seu melhor amigo</h5>
                <p className="text-sm text-muted-foreground">Use o período de teste grátis para navegar por todas as categorias. Veja se o conteúdo que você gosta (novelas, futebol, filmes) está lá e se a imagem te agrada.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia IPTV para Quem Nunca Utilizou: Comece do Zero"
        subtitle="Está perdido? Nós explicamos tudo do jeito mais fácil. Saiba o que é, como funciona e como ter canais na sua TV hoje."
        heroImage="https://images.unsplash.com/photo-1540655037529-dec987208707?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Se você nunca ouviu falar em IPTV até poucos dias atrás, não se sinta 'atrasado'. A tecnologia de streaming de canais evoluiu muito rápido e é normal ficar confuso com tantos nomes, aparelhos e preços diferentes. A boa notícia é que você está a um passo de economizar muito dinheiro e ter um entretenimento de qualidade superior na sua casa.",
          "Este guia foi escrito especificamente para quem não sabe absolutamente nada sobre o assunto. Esqueça os termos complicados. Vamos falar de TV como ela deve ser: simples, rápida e divertida. Na P2 PRO, atendemos milhares de pessoas que, assim como você, queriam apenas uma solução que funcionasse sem precisar de um curso de informática.",
          "Prepare-se para descobrir que a televisão do futuro já está disponível para você agora. Vamos começar essa jornada juntos?"
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🎁 Quer Experimentar Sem Compromisso?"
        ctaDescription="Oferecemos um teste guiado para iniciantes. Nós instalamos tudo para você remotamente. Experimente a P2 PRO agora."
        ctaButtonText="Quero Meu Teste Guiado"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-iptv-nunca-utilizou")({
  head: () => ({
    meta: [
      { title: "Guia IPTV para Iniciantes: Comece do Absoluto Zero 2026" },
      { name: "description", content: "Nunca usou IPTV? Este guia explica tudo de forma simples. Saiba o que é necessário para ter canais via internet na sua TV sem complicações." },
    ],
  }),
  component: ArticlePage,
});
